import { useMemo, useState } from 'react';
import pLimit from 'p-limit';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { uploadSingleFile } from '@/services/ws-file-uploader/src/main/resources/rest-api';
import type { AttachmentDto } from '@/services/ws-funnel/src/main/resources/rest-api';
import {
  getMailTemplateByPromotionId,
  deleteMailTemplate,
  upsertMailTemplateByPromotionId,
  createMailTemplateAttachments,
} from '@/services/ws-funnel/src/main/resources/rest-api';
import { Form } from '@/js/libs/final-form';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useSubscribe } from '@/js/libs/observable';
import { Loader } from '@/js/libs/ui';
import { useNotification } from '@/js/libs/ui/src/V2';
import { useLazyRequest, useRequest } from '@/js/libs/use-request';

import { useConfirmationLegacy } from 'src/hooks/useConfirmationLegacy';
import { useRequestErrorNotification } from 'src/hooks/useRequestErrorNotification';
import { buildFileName } from 'src/utils/string';
import { usePromotionsRoutes } from '../../hooks';
import type { PromotionDetailsRouteParams } from '../../types';
import { LanguageKeyToLanguageCodeMap } from './constants';
import type { PromotionMailTemplateFormValues } from './form';
import { mailTemplateFormToApi, mailTemplateFormFromApi } from './form';
import { useLanguageState } from './LanguageTabs';
import { MailTemplateForm } from './MailTemplateForm';
import type { UploadFile } from './types';

const limit = pLimit(5);

export const PromotionMailTemplate = () => {
  const showRequestError = useRequestErrorNotification();
  const { promotionId: salesPromotionId } = useParams<PromotionDetailsRouteParams>();
  const { goToPromotionPage } = usePromotionsRoutes();
  const [templateAttachments, setTemplateAttachments] = useState<AttachmentDto[]>([]);
  const [filesToUpload, setFilesToUpload] = useState<UploadFile[]>([]);
  const [processingAttachments, setProcessingAttachments] = useState<boolean>(false);
  const [selectedLanguage, setSelectedLanguage] = useLanguageState();
  const notify = useNotification();

  const templateRequest = useRequest({
    apiMethod: getMailTemplateByPromotionId,
    params: { salesPromotionId },
    onSuccess: ({ data }) => {
      if (data.attachments) {
        setTemplateAttachments(data.attachments);
      }
    },
    onFail: showRequestError,
  });
  const template = useSubscribe(templateRequest.data$, (response) => response?.data);
  const templateLoading = useSubscribe(templateRequest.pending$);

  const initialValues: PromotionMailTemplateFormValues = useMemo(() => {
    if (template) {
      return mailTemplateFormFromApi(template);
    }
    return mailTemplateFormFromApi({ id: '', promotionId: '' });
  }, [template]);

  const { t } = useTranslation(['backend', 'common']);
  const confirm = useConfirmationLegacy();

  const uploadSingleFileRequest = useLazyRequest({
    apiMethod: uploadSingleFile,
    cancelPreviousRequests: false,
  });

  const createMailTemplateAttachmentsRequest = useLazyRequest({
    apiMethod: createMailTemplateAttachments,
    cancelPreviousRequests: false,
  });
  const uploading = useSubscribe(createMailTemplateAttachmentsRequest.pending$);

  const updateTemplateRequest = useLazyRequest({
    apiMethod: upsertMailTemplateByPromotionId,
    onFail: showRequestError,
  });
  const updating = useSubscribe(createMailTemplateAttachmentsRequest.pending$);

  const removeTemplateRequest = useLazyRequest({
    apiMethod: deleteMailTemplate,
    onFail: showRequestError,
  });
  const removing = useSubscribe(removeTemplateRequest.pending$);

  const isLoading = processingAttachments || uploading || updating || removing;

  const createOrUpdate = useCallbackRef(async (form: PromotionMailTemplateFormValues) => {
    try {
      setProcessingAttachments(true);

      let uploadedAttachments: AttachmentDto[] = [];

      if (filesToUpload.length) {
        const results = await Promise.all(
          filesToUpload.map(({ file }) =>
            limit(async () => {
              const { data: fileInfo } = await uploadSingleFileRequest.load({ file });

              const { data: createdAttachments } = await createMailTemplateAttachmentsRequest.load({
                attachmentForms: [
                  {
                    fileId: fileInfo.id,
                    language: LanguageKeyToLanguageCodeMap[selectedLanguage],
                    name: buildFileName(fileInfo.name, fileInfo.extension),
                  },
                ],
              });

              return createdAttachments;
            }),
          ),
        );

        uploadedAttachments = results.flat();
      }

      const attachmentIds = [...templateAttachments, ...uploadedAttachments].map((attachment) => attachment.id);
      const formApiValues = mailTemplateFormToApi(form, attachmentIds);

      await updateTemplateRequest.load({
        mailTemplate: formApiValues,
        salesPromotionId,
      });

      goToPromotionPage(salesPromotionId);
    } catch (error) {
      const msg = error instanceof Error ? error.message : t('common:UNKNOWN_ERROR');
      notify.error({ body: msg });
    } finally {
      setProcessingAttachments(false);
    }
  });

  const remove = useCallbackRef(async () => {
    if (await confirm(t('serviceDashboard:REMOVE_PROMOTION_MAIL_TEMPLATE'), t('serviceDashboard:REMOVE_PROMOTION_MAIL_TEMPLATE_MESSAGE'))) {
      await removeTemplateRequest.load({ salesPromotionId });
      goToPromotionPage(salesPromotionId);
    }
  });

  if (templateLoading) {
    return <Loader />;
  }

  return (
    <Form<PromotionMailTemplateFormValues>
      onSubmit={createOrUpdate}
      initialValues={initialValues}
      render={({ handleSubmit, submitting }) => (
        <MailTemplateForm
          onSave={handleSubmit}
          isSaving={submitting || isLoading}
          template={template}
          onRemove={remove}
          templateAttachments={templateAttachments}
          setTemplateAttachments={setTemplateAttachments}
          filesToUpload={filesToUpload}
          setFilesToUpload={setFilesToUpload}
          uploading={processingAttachments || uploading}
          selectedLanguage={selectedLanguage}
          setSelectedLanguage={setSelectedLanguage}
        />
      )}
    />
  );
};
